<template>
  <q-card>
    <q-card-section
      horizontal
      class="row col-12"
      :data-e2e="`card-scheduled-session-${session.sessionName}`"
    >
      <q-carousel
        v-if="hasSessionAppointments"
        v-model="selectedAppointment"
        transition-prev="slide-right"
        transition-next="slide-left"
        swipeable
        animated
        control-color="grey-7"
        arrows
        class="col-xs-4"
      >
        <q-carousel-slide
          v-for="appointment in appointments"
          :key="appointment.id"
          :name="appointment.id"
          class="column no-wrap flex-center q-pa-none"
        >
          <div
            class="row full-width no-select"
          >
            <div
              class="row col-12 bg-secondary text-white justify-center
               text-bold text-overline text-uppercase items-center q-py-none"
            >
              {{ getAppointmentWeekdayFormatted(appointment) }}
            </div>
            <div
              class="row col-12 justify-center items-end q-py-xs calendar-day"
            >
              <div
                :class="{
                  'text-grey-7': isAppointmentReady(appointment),
                  'text-grey-4': !isAppointmentReady(appointment)
                }"
                class="text-h6 q-pr-xs"
              >
                {{ getAppointmentMonthFormatted(appointment) }}
              </div>
              <div
                :class="{
                  'text-dark': isAppointmentReady(appointment),
                  'text-grey-5': !isAppointmentReady(appointment)
                }"
                class="text-h3 text-dark"
              >
                {{ getAppointmentDayFormatted(appointment) }}
              </div>
            </div>
            <div
              :class="{
                'text-dark-variant': isAppointmentReady(appointment),
                'text-grey-5': !isAppointmentReady(appointment)
              }"
              class="row col-12 q-py-none text-caption bg-grey-2 justify-center items-center "
            >
              {{ getAppointmentTimeFormatted(appointment) }}
            </div>
            <div
              :class="{
                'text-dark-variant': isAppointmentReady(appointment),
                'text-grey-5': !isAppointmentReady(appointment)
              }"
              class="row col-12  text-dark-variant text-caption bg-grey-2 justify-center items-center q-py-none"
            >
              <div class="ellipsis q-mx-sm">
                {{ appointment.userName || '&nbsp;' }}
              </div>
            </div>
            <q-separator
              v-if="showAppointmentNotes(appointment)"
              style="width: 100%"
            />
            <div
              v-if="showAppointmentNotes(appointment)"
              class="row col-12 text-dark-variant text-caption bg-grey-2 justify-center items-center q-py-none"
            >
              <appointment-notes :notes="appointment.notes" />
            </div>
          </div>
        </q-carousel-slide>
      </q-carousel>

      <q-card-section
        class="col q-ma-none q-pa-none column"
      >
        <q-card-section
          class="row col content-start items-center q-py-md full-width"
        >
          <div
            class="col"
          >
            <div
              class="text-subtitle2 ellipsis"
            >
              <span>{{ sessionNameFormatted }}</span>
            </div>
          </div>
          <q-space />
          <div class="col-auto">
            <q-btn
              v-if="hasNotificationSettings"
              size="sm"
              flat
              round
              :icon="sessionNotificationIcon"
              :disable="offlineMode"
              @click.stop
            >
              <q-menu
                @hide="onCloseCustomNotificationMenu"
              >
                <div
                  class="q-pa-xs text-center text-subtitle2 bg-primary text-white"
                >
                  {{ $t('sessions.sessionCard.textTitleNotifications') }}
                </div>
                <q-separator />
                <q-list>
                  <q-item
                    v-close-popup
                    :active="notificationSettings.type === 'all'"
                    clickable
                    :disable="usingFakeRole"
                    @click="onUpdateNotifications({type: 'all', custom: []})"
                  >
                    <q-item-section avatar>
                      <q-icon name="notifications_active" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textNotificationsOn') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    :active="notificationSettings.type === 'custom'"
                    clickable
                  >
                    <q-item-section avatar>
                      <q-icon name="notifications_none" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textNotificationsCustom') }}
                    </q-item-section>
                    <q-item-section side>
                      <q-icon name="keyboard_arrow_right" />
                    </q-item-section>
                    <q-menu>
                      <q-item
                        v-for="notificationEventType in customNotificationEventTypes"
                        :key="notificationEventType.value"
                        :disable="usingFakeRole"
                      >
                        <q-item-section>
                          <q-toggle
                            v-model="notificationSettings.custom"
                            :label="notificationEventType.label"
                            :val="notificationEventType.value"
                            :disable="usingFakeRole"
                            checked-icon="notifications_none"
                            color="green"
                            unchecked-icon="notifications_off"

                            @update:model-value="onUpdateNotifications({type: 'custom'})"
                          />
                        </q-item-section>
                      </q-item>
                      <q-separator />
                      <q-item>
                        <q-item-section>
                          <q-toggle
                            v-model="notificationSettings.notifyImmediately"
                            :label="$t('sessions.sessionCard.labelNotifyImmediately')"
                            :val="true"
                            :disable="usingFakeRole"
                            checked-icon="flash_on"
                            color="green"
                            unchecked-icon="watch_later"

                            @update:model-value="onUpdateNotifications({type: 'custom'})"
                          >
                            <q-tooltip>
                              {{ $t('sessions.sessionCard.textNotifyImmediatelyHint') }}
                            </q-tooltip>
                          </q-toggle>
                        </q-item-section>
                      </q-item>
                    </q-menu>
                  </q-item>
                  <q-item
                    v-close-popup
                    :active="notificationSettings.type === 'none'"
                    clickable
                    :disable="usingFakeRole"
                    @click="onUpdateNotifications({type: 'none', custom: []})"
                  >
                    <q-item-section avatar>
                      <q-icon name="notifications_off" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textNotificationsOff') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
            <q-btn
              v-if="userCanSeeMenu && !sessionIsRunning"
              size="sm"
              flat
              round
              icon="more_vert"
              :data-e2e="`button-session-menu-${session.sessionName}`"
              @click.stop
            >
              <q-menu
                v-model="showMenu"
                @hide="onCloseVisibilityMenu"
              >
                <q-list
                  class="text-dark"
                  style="min-width: 100px"
                >
                  <q-item
                    v-if="userCanEditScheduledSession"
                    v-close-popup
                    :clickable="!sessionIsRunning"
                    :disable="usingFakeRole || offlineMode"
                    @click="onEditSession"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="edit"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textEditSession') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="userCanEditScheduledSession"
                    v-close-popup
                    :clickable="!sessionIsRunning"
                    :disable="usingFakeRole || offlineMode"
                    @click="onEditSessionSchedule"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="calendar_month"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textEditSessionSchedule') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="userCanEditSessionVisibility"
                    :disable="offlineMode"
                    :clickable="!sessionIsRunning"
                  >
                    <q-item-section avatar>
                      <q-icon name="visibility" />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textVisibility') }}
                    </q-item-section>
                    <q-item-section side>
                      <q-icon name="keyboard_arrow_right" />
                    </q-item-section>
                    <q-menu v-if="!offlineMode">
                      <q-item>
                        <q-item-section>
                          <q-toggle
                            :model-value="visibilitySettings.limitedVisibility"
                            :label="$t('sessions.sessionCard.toggleLimitVisibilityLabel')"
                            checked-icon="visibility"
                            :val="visibilitySettings.value"
                            :disable="usingFakeRole"
                            color="green"
                            unchecked-icon="visibility_off"
                            @update:model-value="onUpdateVisibilitySetting({ limitedVisibility: $event }, true)"
                          />
                        </q-item-section>
                      </q-item>
                      <template
                        v-if="visibilitySettings.limitedVisibility"
                      >
                        <q-separator />
                        <q-item
                          v-for="visibilityOption in visibilityOptions"
                          :key="visibilityOption.value"
                          :disable="usingFakeRole || visibilityOption.disable"
                          tag="label"
                        >
                          <q-item-section
                            class="col-auto"
                          >
                            <q-toggle
                              :model-value="visibilitySettings.visibility"
                              :val="visibilityOption.value"
                              :disable="usingFakeRole || visibilityOption.disable"
                              checked-icon="visibility"
                              color="green"
                              unchecked-icon="visibility_off"
                              @update:model-value="onToggleVisibility(visibilityOption.value)"
                            />
                          </q-item-section>
                          <q-item-section class="col">
                            <q-item-label>
                              {{ visibilityOption.label }}
                            </q-item-label>
                            <q-item-label caption>
                              {{ visibilityOption.hint }}
                            </q-item-label>
                          </q-item-section>
                        </q-item>
                        <q-separator />
                        <q-item>
                          <q-item-section>
                            <q-toggle
                              :model-value="visibilitySettings.limitHistoryVisibility"
                              :label="$t('sessions.sessionCard.toggleLimitHistoryVisibilityLabel')"
                              :true-value="false"
                              :false-value="true"
                              :disable="usingFakeRole"
                              checked-icon="visibility"
                              unchecked-icon="visibility_off"
                              color="green"
                              @update:model-value="onUpdateVisibilitySetting({ limitHistoryVisibility: $event })"
                            />
                          </q-item-section>
                        </q-item>
                      </template>
                    </q-menu>
                  </q-item>
                  <q-item
                    v-if="userCanDeleteScheduledSession"
                    v-close-popup
                    clickable
                    :disable="usingFakeRole || offlineMode"
                    data-e2e="list-item-delete-session"
                    @click="onDeleteSession"
                  >
                    <q-item-section
                      avatar
                      class="text-no-wrap"
                    >
                      <q-icon
                        name="delete"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('sessions.sessionCard.textDeleteSession') }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    @click="onCopySupportInfo({ 'appointment id': selectedAppointment })"
                  >
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="content_copy"
                      />
                    </q-item-section>
                    <q-item-section class="text-no-wrap">
                      {{ $t('common.copySupportInfo') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </q-card-section>

        <q-card-section
          v-if="sessionIsRunning"
          class="row items-center q-py-md"
        >
          <div class="row items-center col no-wrap">
            <q-spinner-dots
              color="primary"
              size="2em"
              class="q-mr-md"
            />
            <span class="ellipsis">{{ session.runByName }}</span>
            <q-tooltip>
              {{ $t('sessions.sessionCard.textSessionInProgress') }}
            </q-tooltip>
          </div>
          <div
            v-if="userCanSuperviseSession"
          >
            <q-btn
              flat
              :label="superviseLabel"
              :disable="!isSubscriptionPremium || offlineMode"
              icon="supervised_user_circle"
              color="warning"
              :data-e2e="`button-supervise-session-${session.sessionName}`"
              @click.stop="onSuperviseSession"
            >
              <q-badge
                v-if="!isSubscriptionPremium"
                floating
                color="transparent"
                transparent
                outline
              >
                <q-icon
                  name="workspace_premium"
                  color="dark-variant"
                />
                <q-tooltip
                  v-if="!isSubscriptionPremium"
                >
                  {{ $t('premiumUpgrade.upgradeHint') }}
                </q-tooltip>
              </q-badge>
            </q-btn>
          </div>
          <template
            v-if="!isSupervisingByCurrentUser"
          >
            <div
              v-if="isSuspendedSession"
              class="row items-center justify-end col-auto"
            >
              <universal-tooltip
                :show-tooltip="!userCanStartSession"
                :text="$t('sessions.sessionCard.disabledResumeSession')"
              >
                <q-btn
                  unelevated
                  :label="$t('sessions.sessionCard.textResumeSession')"
                  :disable="!userCanStartSession"
                  icon="screen_share"
                  color="positive"
                  @click.stop="onResumeSuspendedSession"
                />
              </universal-tooltip>
            </div>
            <div
              v-else
              class="row items-center justify-end col-auto"
            >
              <universal-tooltip
                :show-tooltip="!userCanStartSession"
                :text="tooltipText"
              >
                <q-btn
                  unelevated
                  :label="joinLabel"
                  :disable="disableWatchMode"
                  :icon="joinIcon"
                  color="positive"
                  :data-e2e="`button-${e2eLabelForJoinButton}-session-${session.sessionName}`"
                  @click.stop="onEnterSession"
                />
              </universal-tooltip>
            </div>
          </template>
        </q-card-section>

        <q-card-section
          v-else-if="['scheduled', 'created'].includes(session.status)"
          class="row justify-end items-center q-py-md"
        >
          <div
            v-if="!hasSessionAppointments"
            class="col-xs-12 col-sm"
          >
            <span
              v-for="(weekday, weekdayIndex) in weekdays"
              :key="weekdayIndex"
            >
              <transition
                v-if="weekdayHasSession(weekdayIndex)"
                appear
                enter-active-class="animated bounceIn slower delay-2s"
              >
                <q-chip
                  :size="weekdaySize(weekdayIndex)"
                  :class="weekdayClass(weekdayIndex)"
                  square
                >
                  {{ weekday }}
                </q-chip>
              </transition>
              <q-chip
                v-else
                :size="weekdaySize(weekdayIndex)"
                :class="weekdayClass(weekdayIndex)"
                square
              >
                {{ weekday }}
              </q-chip>
            </span>
          </div>
          <div
            class="col-auto"
          >
            <universal-tooltip
              :show-tooltip="!userCanStartSession"
              :text="$t('sessions.sessionCard.disabledStartSession')"
            >
              <q-btn-dropdown
                v-if="canStartGroupSession || userCanAddSessionProgramsData"
                unelevated
                :label="$t('sessions.sessionCard.buttonStartSession')"
                :disable="!userCanStartSession"
                icon="slideshow"
                color="positive"
                split
                :data-e2e="`button-start-session-${session.sessionName}`"
                @click.stop="onStartSession(false)"
              >
                <q-list>
                  <q-item
                    v-if="canStartGroupSession"
                    v-close-popup
                    clickable
                    :disable="offlineMode || !hasActiveSessionPrograms"
                    :data-e2e="`button-start-group-session-${session.sessionName}`"
                    @click="onStartSession(true)"
                  >
                    <q-item-section avatar>
                      <q-icon
                        name="group"
                      />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>
                        {{ $t('sessions.sessionCard.buttonStartGroupSession') }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-if="userCanAddSessionProgramsData"
                    v-close-popup
                    :clickable="!sessionIsRunning"
                    :disable="disableAddManualSessionEntry"
                    @click="onAddManualSessionEntry"
                  >
                    <q-tooltip v-if="hasActiveSessionPrograms && disableAddManualSessionEntry">
                      {{ $t('sessions.permissions.textCannotEditSessions') }}
                    </q-tooltip>
                    <q-item-section
                      avatar
                    >
                      <q-icon
                        name="add"
                      />
                    </q-item-section>
                    <q-item-section>
                      {{ $t('sessions.sessionCard.textAddManualSessionEntry') }}
                    </q-item-section>
                  </q-item>
                  <q-tooltip
                    v-if="!hasActiveSessionPrograms"
                    class="desktop-tooltip"
                  >
                    {{ $t('run.overview.noProgramsText') }}
                  </q-tooltip>
                </q-list>
              </q-btn-dropdown>
              <q-btn
                v-else
                unelevated
                :disable="!userCanStartSession"
                :label="$t('sessions.sessionCard.buttonStartSession')"
                icon="slideshow"
                color="positive"
                @click.stop="onStartSession(false)"
              >
                <q-tooltip
                  v-if="!userCanStartSession"
                  class="desktop-tooltip"
                >
                  {{ $t('sessions.sessionCard.disabledStartSession') }}
                </q-tooltip>
              </q-btn>
            </universal-tooltip>
          </div>
        </q-card-section>
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
<script>
import AppointmentNotes from 'components/Appointments/AppointmentNotes';
import EditSessionActivityDialog from 'components/SessionActivities/Modals/EditSessionActivityDialog';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { chain, find, get, isDate, isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { ACTION_NOT_AVAILABLE_REASONS, useNotifications } from 'src/composables/notifications';
import { useSessionActivities } from 'src/composables/sessionActivities';
import { useTime } from 'src/composables/time';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import UserNotificationsDB from 'src/fb/userNotifications-db';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionCardsCommon from 'src/mixins/SessionCardsCommon';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { geolocation } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { sessions } from 'src/store/sessions';
import { userRoles } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'ScheduledSessionCard',

  components: {
    AppointmentNotes,
    UniversalTooltip,
  },

  mixins: [Auth, Interactions, SessionCardsCommon, SessionActivitiesMixin, Toolbox],

  props: {
    fakeRole: {
      type: [Object, null],
      default: null,
    },
    appointments: {
      type: Array,
      default: () => ([]),
    },
    initialAppointment: {
      type: String,
      default: null,
    },
    checkPendingActions: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { goToNotification, isNotificationActionAvailable } = useNotifications();
    const { checkUserCanStartSessionActivity } = useSessionActivities();
    const { getLocaleDateString, getLocaleTimeString, getDate, addToDate, isBetweenDates } = useTime();

    return {
      goToNotification,
      isNotificationActionAvailable,
      checkUserCanStartSessionActivity,
      getLocaleDateString,
      getLocaleTimeString,
      getDate,
      addToDate,
      isBetweenDates,
    };
  },

  data() {
    return {
      sessionPrograms: [],
      activeSessionPrograms: [],
      selectedAppointment: null,

      showMenu: false,

      customNotificationEventTypes: [
        {
          value: 'sessionFinished',
          label: this.$t('activityFeed.events.sessionFinished'),
        },
        // @todo: HR-427
        /* {
          value: 'sessionNotesAdded',
          label: this.$t('activityFeed.events.sessionNotesAdded'),
        }, */
        {
          value: 'sessionFilesAttached',
          label: this.$t('activityFeed.events.filesAttached'),
        },
        {
          value: 'targetMastered',
          label: this.$t('activityFeed.events.targetMastered'),
        },
        {
          value: 'targetReopened',
          label: this.$t('activityFeed.events.targetReopened'),
        },
      ],

    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'appointmentThreshold',
      'hasTrainerCredentials',
      'isSeniorTherapist',
      'isParent',
      'isSubscriptionPremium',
    ]),
    ...mapState(sessions, ['activeVideoCallSessions']),
    ...mapState(network, ['networkOnline', 'offlineMode']),
    ...mapState(programs, ['programs']),

    userCanSuperviseSession() {
      return this.userHasPrivilege('sessions/superviseSessions')
        && (this.userDetails.userId !== this.session.runBy)
        && (this.isSupervisingByCurrentUser || (!this.session.supervisedBy && this.session.runBy !== null));
    },

    userCanAddSessionProgramsData() {
      return this.userCanStartSession
        && this.userHasPrivilege('sessions/enterManualSessions');
    }, // userCanAddSessionProgramsData()

    hasSessionAppointments() {
      return !isEmpty(this.appointments);
    },

    usingFakeRole() {
      return !!this.fakeRole;
    }, // usingFakeRole()

    userCanStartSession() {
      return !this.usingFakeRole && this.userHasPrivilege('sessions/startSessions');
    }, // userCanStartSession()

    selectedAppointmentData() {
      return find(this.appointments, { id: this.selectedAppointment });
    },

    /**
     * Returns the selected session appointment if it is today, otherwise null.
     * This prevents the user from using an appointment that is not scheduled for today.
     */
    sessionAppointment() {
      if (!this.selectedAppointmentData || this.selectedAppointmentData.isRecurring) {
        return null;
      }
      return this.isAppointmentReady(this.selectedAppointmentData) ? this.selectedAppointment : null;
    },

    canStartGroupSession() {
      return (this.session.status === 'scheduled')
        && !this.isInvitedUser
        && this.userCanStartSession;
    }, // canStartGroupSession()

    joinLabel() {
      if (this.session.runBy === this.userDetails.userId) {
        return this.$t('sessions.sessionCard.textResumeSession');
      }
      if (this.isInvitedUser) {
        return this.$t('sessions.sessionCard.textJoinVideoSession');
      }
      return this.$t('sessions.sessionCard.textWatchSession');
    }, // joinLabel()

    joinIcon() {
      if (this.isInvitedUser) {
        return 'voice_chat';
      }
      return 'screen_share';
    }, // joinIcon()

    joinRoute() {
      if (!this.sessionIsRunning) {
        return '';
      }

      return {
        name: 'session-data-collection',
        params: { sessionId: this.session.id },
      };
    },

    disableWatchMode() {
      return (this.joinLabel !== this.$t('sessions.sessionCard.textResumeSession') && this.offlineMode)
          || !this.userCanStartSession;
    },

    superviseLabel() {
      if (this.isSupervisingByCurrentUser) {
        return this.$t('sessions.sessionCard.textResumeSupervision');
      }
      return this.$t('sessions.sessionCard.textSupervise');
    },

    isSuspendedSession() {
      return this.session.runBy === null;
    },

    e2eLabelForJoinButton() {
      if (this.session.runBy === this.userDetails.userId) return 'resume';
      if (this.isInvitedUser) return 'join';
      return 'watch';
    },

    isSupervisingByCurrentUser() {
      return this.session.supervisedBy === this.userDetails.userId;
    },

    isInvitedUser() {
      const videoSession = find(this.activeVideoCallSessions, { sessionId: this.session?.id });
      return videoSession ? videoSession.runBy !== this.userDetails?.userId : false;
    },

    clientName() {
      return get(this.client, 'clientName', null);
    },

    tooltipText() {
      if (this.session.runBy === this.userDetails.userId) {
        return this.$t('sessions.sessionCard.disabledResumeSession');
      }

      return this.$t('sessions.sessionCard.disabledWatchMode');
    },

    hasActiveSessionPrograms() {
      return this.activeSessionPrograms.length;
    },

    disableAddManualSessionEntry() {
      return !this.hasActiveSessionPrograms || this.usingFakeRole;
    },
  },

  watch: {
    session: {
      deep: false,
      immediate: true,
      async handler(session) {
        this.loadNotificationSettings(session.id);
        this.setVisibilitySettings(session);
        await this.loadSessionPrograms(session.id);
      },
    },

    appointments: {
      // don't need deep watching here for our logic as we are waiting the array to be assigned, not mutated
      deep: false,
      immediate: true,
      handler() {
        this.setInitialAppointment();
      },
    },

    initialAppointment: {
      immediate: true,
      handler() {
        this.setInitialAppointment();
      },
    },
  },

  methods: {
    ...mapActions(auth, ['changeCurrentClientById']),
    ...mapActions(sessions, ['duplicateSession']),
    ...mapActions(geolocation, ['refreshGeolocation']),

    isSelectedAppointmentOwn() {
      return this.selectedAppointmentData?.user === this.userDetails.userId;
    },

    async switchToSessionClient() {
      this.$q.loading.show();
      await this.changeCurrentClientById(this.session.client);
      this.$q.loading.hide();
    },

    async onResumeSuspendedSession() {
      if (!this.networkOnline && !this.offlineMode) {
        return this.alertDialog(
          this.$t('common.noInternetConnection'),
          this.$t('sessions.sessionCard.warningBadConnectionToEnterSession'),
        );
      }

      if (!this.userCanStartSession) {
        return;
      }

      this.refreshGeolocation(true);
      const sessionsDb = new SessionsDB();
      await sessionsDb.resumeSuspendedSession(
        this.session,
        this.userDetails,
        this.isSelectedAppointmentOwn() ? this.sessionAppointment || null : null,
      );

      return this.$router.push(this.joinRoute);
    },

    async onStartSession(groupSession = false) {
      try {
        if (!this.userCanStartSession) {
          return;
        }

        this.$q.loading.show();

        if (!await this.checkUserCanStartSessionActivity(this.session.client || null)) {
          return;
        }

        if (this.checkPendingActions && !this.offlineMode) {
          const pendingActionNotification = await this.findPendingActionNotificationThatPreventsSessionStart();

          if (pendingActionNotification) {
            const { available, reason } = await this.isNotificationActionAvailable(pendingActionNotification);

            if (available) {
              this.confirmDialog(
                this.$t('sessions.sessionCard.completeActionNotificationBeforeStartingSession.title'),
                this.$t('sessions.sessionCard.completeActionNotificationBeforeStartingSession.message'),
                () => this.goToNotification(pendingActionNotification),
                {
                  ok: {
                    label: this.$t('sessions.sessionCard.completeActionNotificationBeforeStartingSession.button'),
                    flat: true,
                  },
                },
              );
              return;
            }

            const userNotificationsDb = new UserNotificationsDB();

            if (
              [ACTION_NOT_AVAILABLE_REASONS.RESOURCE_DELETED, ACTION_NOT_AVAILABLE_REASONS.UNKNOWN].includes(reason)
            ) {
              await userNotificationsDb.delete(pendingActionNotification.id);
            } else if (
              reason === ACTION_NOT_AVAILABLE_REASONS.ACTION_COMPLETED
              && !pendingActionNotification.actionCompleted
            ) {
              await userNotificationsDb.set({
                actionCompleted: true,
                actionCompletedTimestamp: serverTimestamp(),
                viewed: true,
              }, pendingActionNotification.id);
            }
          }
        }

        if (this.session.client !== get(this.currentClient, 'id')) {
          await this.switchToSessionClient();
        }

        const overviewRoute = {
          name: groupSession ? 'Prepare Group Session' : 'Session Overview',
          params: { sessionId: this.session.id },
        };

        if (this.sessionAppointment) {
          overviewRoute.query = { appointment: this.sessionAppointment };
        }

        this.$router.push(overviewRoute);
      } catch (err) {
        this.notifyNegative(err.message);
        console.error(err);
        logEvent(err, {
          context: {
            context_data: {
              sessionId: this.session.id,
            },
          },
        });
      } finally {
        this.$q.loading.hide();
      }
    }, // onStartSession()

    async onEnterSession() {
      if (!this.networkOnline && !this.offlineMode) {
        return this.alertDialog(
          this.$t('common.noInternetConnection'),
          this.$t('sessions.sessionCard.warningBadConnectionToEnterSession'),
        );
      }

      if (!this.userCanStartSession) {
        return this.alertDialog(
          this.$t('common.insufficientPrivileges'),
          this.$t('sessions.sessionCard.disabledStartSession'),
        );
      }
      if (this.session.client !== get(this.currentClient, 'id')) {
        await this.switchToSessionClient();
      }
      if (this.session.runBy === this.userDetails.userId) {
        this.refreshGeolocation(true);
      }
      this.$router.push(this.joinRoute);
    }, // onEnterSession()

    async onSuperviseSession() {
      if (!this.networkOnline && !this.offlineMode) {
        return this.alertDialog(
          this.$t('common.noInternetConnection'),
          this.$t('sessions.sessionCard.warningBadConnectionToEnterSession'),
        );
      }

      if (!this.isSubscriptionPremium) {
        return;
      }

      if (!this.isSupervisingByCurrentUser
        && !await this.checkUserCanStartSessionActivity(this.session.client || null)) {
        return;
      }

      this.refreshGeolocation(true);

      const supervisionRoute = {
        name: 'supervise-session',
        params: { sessionId: this.session.id },
      };

      if (this.sessionAppointment && this.selectedAppointmentData.user === this.userDetails.userId) {
        supervisionRoute.query = { appointment: this.sessionAppointment };
      }

      this.$router.push(supervisionRoute);
    },

    onAddManualSessionEntry() {
      this.addManualSessionData();
    }, // onAddManualSessionEntry()

    async addManualSessionData() {
      const startTimestamp = Timestamp.fromDate(new Date(Date.now() - 60 * 60 * 1000));
      const endTimestamp = Timestamp.fromDate(new Date(Date.now()));
      const sessionActivity = {
        startTimestamp,
        endTimestamp,
        status: 'finished',
        type: 'session',
        user: this.userDetails.userId,
        userName: this.userDetails.name,
        client: this.session.client,
        clinic: this.session.clinic,
        notes: '',
        sessionDurationOffset: 0,
        sessionNoteTemplateId: this.session.sessionNoteTemplate,
        version: 2,
        isDeleted: false,
      };
      // Show session activity timing dialog
      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity,
        title: this.$t('sessions.sessionCard.textAddManualSessionEntry'),
      }, async (sessionActivityChanges) => {
        if ((!(get(sessionActivityChanges, 'startTimestamp') instanceof Timestamp))
          || (!(get(sessionActivityChanges, 'endTimestamp') instanceof Timestamp))) {
          return;
        }
        const newSessionActivity = {
          ...sessionActivity,
          ...sessionActivityChanges,
        };
        const duration = (
          newSessionActivity.endTimestamp.toDate()
          - newSessionActivity.startTimestamp.toDate()
        ) / 1000;
        newSessionActivity.sessionDurationEnd = duration;

        // Duplicate and make session ready
        this.$q.loading.show();
        const newSessionId = await this.duplicateSession({
          sessionId: this.session.id,
          sessionProps: {
            shuffle: false,
            originalSessionId: this.session.id,
            currentProgram: null,
            runBy: newSessionActivity.user,
            runByName: newSessionActivity.userName,
            runTimestamp: newSessionActivity.startTimestamp,
            finishTimestamp: newSessionActivity.endTimestamp,
            supervisedBy: null,
            supervisedByName: null,
            duration,
            hasTimecodedData: true,
            status: 'done',
          },
        });

        const sessionsDb = new SessionsDB();
        await sessionsDb.logScheduledSessionHistory(this.session.id, newSessionId, 60);

        const sessionActivitiesDb = new SessionActivitiesDB();
        await sessionActivitiesDb.create({
          ...newSessionActivity,
          session: newSessionId,
          sessionName: this.session.sessionName || null,
          appointment: null,
          createdBy: this.userDetails.userId,
        });

        // Redirect to history
        this.$router.push({
          name: 'History View - Edit',
          params: { sessionId: newSessionId },
        });
        this.$q.loading.hide();
      });
    }, // addManualSessionData()

    setInitialAppointment() {
      // @todo ID might not be right - might be key
      if ((this.initialAppointment !== null) && (find(this.appointments, { id: this.initialAppointment }))) {
        this.selectedAppointment = this.initialAppointment;
        return;
      }

      const readyAppointments = this.appointments.filter((appointment) => this.isAppointmentReady(appointment));

      if (this.userCanSuperviseSession && !this.isSupervisingByCurrentUser) {
        this.selectedAppointment = readyAppointments
          .find((appointment) => {
            return appointment.user === this.userDetails.userId;
          })
          ?.id;

        if (this.selectedAppointment) {
          return;
        }
      }

      this.selectedAppointment = chain(readyAppointments)
        .head()
        // @todo ID might not be right - might be key
        .get('id', chain(this.appointments).head().get('id', null).value())
        .value();
    },

    getAppointmentWeekdayFormatted(appointment) {
      if (!(appointment.scheduledStartTimestamp instanceof Timestamp)) {
        return '';
      }
      return this.getLocaleDateString(appointment.scheduledStartTimestamp, { options: { weekday: 'long' } });
    },

    getAppointmentMonthFormatted(appointment) {
      if (!(appointment.scheduledStartTimestamp instanceof Timestamp)) {
        return '';
      }
      return this.getLocaleDateString(appointment.scheduledStartTimestamp, { options: { month: 'short' } });
    },

    getAppointmentDayFormatted(appointment) {
      if (!(appointment.scheduledStartTimestamp instanceof Timestamp)) {
        return '';
      }
      return this.getDate(appointment.scheduledStartTimestamp);
    },

    getAppointmentTimeFormatted(appointment) {
      if (!(appointment.scheduledStartTimestamp instanceof Timestamp)) {
        return '&nbsp;';
      }
      const scheduledStartDate = appointment.scheduledStartTimestamp.toDate();
      const scheduledEndDate = this.addToDate(scheduledStartDate, { seconds: appointment.duration });
      return `${this.getLocaleTimeString(scheduledStartDate, { options: { hour: 'numeric', minute: '2-digit' } })
      } ${this.$t('common.to')} ${this.getLocaleTimeString(scheduledEndDate, {
        options: { hour: 'numeric', minute: '2-digit' },
      })}`;
    },

    isAppointmentReady(appointment) {
      // If appointment is already attached to this session then should be shown as ready
      if ((this.sessionIsRunning) && (appointment.session === this.session.id)) {
        return true;
      }

      if (!(appointment.scheduledStartTimestamp instanceof Timestamp)) {
        return false;
      }
      const scheduledStartDate = appointment.scheduledStartTimestamp.toDate();
      const scheduledEndDate = this.addToDate(scheduledStartDate, { seconds: appointment.duration });

      if (!isDate(scheduledStartDate) || !isDate(scheduledEndDate)) {
        return false;
      }
      const currentDate = new Date();
      const timeFromAppointmentStart = (scheduledStartDate.getTime() - Date.now()) / 1000;
      const timeAfterAppointmentEnd = (Date.now() - scheduledEndDate.getTime()) / 1000;

      return this.isBetweenDates(
        scheduledStartDate,
        currentDate,
        currentDate,
        { onlyDate: true, inclusiveFrom: true, inclusiveTo: true },
      )
        && (timeFromAppointmentStart <= this.appointmentThreshold)
        && (timeAfterAppointmentEnd <= this.appointmentThreshold);
    },

    async loadSessionPrograms(sessionId) {
      const sessionProgramsDb = new SessionProgramsDB(sessionId);
      this.sessionPrograms = await sessionProgramsDb.readOrdered('order');

      const sessionProgramsIds = this.sessionPrograms.map((sessionProgram) => sessionProgram.program);
      const programsIds = this.programs.reduce((acc, { id, status }) => {
        if (ACTIVE_PROGRAM_STATUSES.includes(status)) {
          acc.push(id);
        }
        return acc;
      }, []);

      this.activeSessionPrograms = sessionProgramsIds.filter((id) => programsIds.includes(id));
    }, // loadSessionPrograms()

    showAppointmentNotes(appointment) {
      return appointment.notes && (appointment.isNotesVisible ?? true) && this.hasTrainerCredentials
        && this.fakeRole?.role !== userRoles.PARENT;
    }, // showAppointmentNotes()

  },
};
</script>
<style lang="scss" scoped>

.video-session-icon {
  opacity: 40%;
  font-size: 28px;
  margin-right: 10px;
}

.calendar-day {
  border-right: solid $grey-2 1px;
}
</style>
